import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarCheck2, Loader2, Plus, Save, Trash2, Upload } from "lucide-react";

const TYPES = { FERIADO: "Feriado", RECESSO: "Recesso/Férias", SEM_AULA: "Sem aula", LETIVO_EXTRA: "Dia letivo extra" };
const WD = { 1: "Segunda", 2: "Terça", 3: "Quarta", 4: "Quinta", 5: "Sexta" };
const fmt = (d) => d.split("-").reverse().join("/");

const DayRow = ({ d, onChange, onDelete }) => (
  <tr className="border-b border-slate-100" data-testid={`cal-day-${d.date}`}>
    <td className="py-1.5 pr-2 font-medium text-slate-700">{fmt(d.date)}</td>
    <td className="py-1.5 pr-2">
      <Select value={d.type} onValueChange={(v) => onChange({ ...d, type: v })}>
        <SelectTrigger className="h-8 w-40"><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
      </Select>
    </td>
    <td className="py-1.5 pr-2">
      {d.type === "LETIVO_EXTRA" && (
        <Select value={String(d.as_weekday || "")} onValueChange={(v) => onChange({ ...d, as_weekday: Number(v) })}>
          <SelectTrigger className="h-8 w-32"><SelectValue placeholder="Horário de..." /></SelectTrigger>
          <SelectContent>{Object.entries(WD).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
      )}
    </td>
    <td className="py-1.5 pr-2"><Input className="h-8 min-w-[160px]" value={d.note || ""} onChange={(e) => onChange({ ...d, note: e.target.value })} /></td>
    <td className="py-1.5 text-xs text-slate-400">{d.source === "IA" ? "IA" : "Manual"}</td>
    <td className="py-1.5"><Button size="icon" variant="ghost" onClick={onDelete} className="h-8 w-8 text-red-600" data-testid={`cal-del-${d.date}`}><Trash2 className="h-4 w-4" /></Button></td>
  </tr>
);

export const SchoolCalendarTab = ({ years, disabled }) => {
  const [yearId, setYearId] = useState("");
  const [days, setDays] = useState(null);
  const [meta, setMeta] = useState({});
  const [busy, setBusy] = useState("");
  const [newDay, setNewDay] = useState({ date: "", type: "FERIADO", note: "" });
  const file = useRef(null);
  useEffect(() => {
    if (yearId || !years?.length) return;
    const cy = new Date().getFullYear();
    const ok = (y) => y.status === "ATIVO" && y.terms?.some((t) => t.start_date);
    setYearId((years.find((y) => ok(y) && Number(y.year) === cy) || years.find(ok) || years[0]).id);
  }, [years, yearId]);
  const load = useCallback(() => {
    if (!yearId) return;
    api.get("/school-calendar", { params: { academic_year_id: yearId } }).then((r) => { setDays(r.data.days); setMeta(r.data); });
  }, [yearId]);
  useEffect(() => { load(); }, [load]);

  const merge = (list) => setDays((cur) => Object.values(Object.fromEntries([...(cur || []), ...list].map((d) => [d.date, d]))).sort((a, b) => a.date.localeCompare(b.date)));
  const upload = async (e) => {
    const fs = [...e.target.files]; e.target.value = "";
    if (!fs.length) return;
    const fd = new FormData(); fd.append("academic_year_id", yearId); fs.forEach((f) => fd.append("files", f));
    setBusy("read");
    try {
      const r = await api.post("/school-calendar/ai-read", fd, { timeout: 300000 });
      merge(r.data.days); setMeta((m) => ({ ...m, file_name: fs.map((f) => f.name).join(", "), pending: true }));
      toast.success(`${r.data.days.length} dia(s) lidos do planejamento. Confira e clique em Salvar calendário.`);
      if (r.data.illegible?.length) toast.warning(`Trechos ilegíveis: ${r.data.illegible.slice(0, 3).join("; ")}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(""); }
  };
  const save = async () => {
    setBusy("save");
    try {
      await api.put("/school-calendar", { academic_year_id: yearId, days, file_name: meta.pending ? meta.file_name : undefined });
      toast.success("Calendário escolar salvo"); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(""); }
  };
  const apply = async () => {
    if (!window.confirm("Calcular e preencher as aulas previstas de TODAS as turmas e disciplinas deste ano? Os valores de aulas previstas atuais serão substituídos.")) return;
    setBusy("apply");
    try {
      const r = await api.post("/school-calendar/apply", { academic_year_id: yearId });
      toast.success(`Aulas previstas preenchidas em ${r.data.filled} diário(s)${r.data.classes_without_schedule ? ` · ${r.data.classes_without_schedule} turma(s) sem quadro de horários` : ""}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(""); }
  };
  const add = () => { merge([{ ...newDay, source: "MANUAL", as_weekday: null }]); setNewDay({ date: "", type: "FERIADO", note: "" }); };
  const off = (days || []).filter((d) => d.type !== "LETIVO_EXTRA").length;

  return (
    <Card className="p-5" data-testid="school-calendar-tab">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-bold text-slate-900">Calendário escolar / Planejamento anual SEEDUC</h3>
          <p className="text-sm text-slate-500">Envie o planejamento (PDF, Excel ou foto): a IA lê feriados, recessos e dias sem aula. Depois calcule as aulas previstas de todas as turmas pelo quadro de horários.</p>
          {meta.file_name && <p className="mt-1 text-xs text-slate-400" data-testid="cal-file-name">Arquivo: {meta.file_name}{meta.pending ? " · ainda não salvo" : meta.updated_by ? ` · salvo por ${meta.updated_by}` : ""}</p>}
        </div>
        <Select value={yearId} onValueChange={setYearId}>
          <SelectTrigger className="w-40" data-testid="cal-year-select"><SelectValue placeholder="Ano letivo" /></SelectTrigger>
          <SelectContent>{(years || []).map((y) => <SelectItem key={y.id} value={y.id}>{y.year}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="mb-4 flex flex-wrap gap-2">
        <input ref={file} type="file" multiple hidden accept=".pdf,.xlsx,.xls,.csv,image/*" onChange={upload} data-testid="cal-file-input" />
        <Button variant="outline" disabled={disabled || !yearId || !!busy} onClick={() => file.current?.click()} data-testid="cal-upload-btn">
          {busy === "read" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />}{busy === "read" ? "Lendo com IA..." : "Enviar planejamento anual"}
        </Button>
        <Button onClick={save} disabled={disabled || !days || !!busy} className="bg-blue-700 hover:bg-blue-800" data-testid="cal-save-btn">
          {busy === "save" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}Salvar calendário
        </Button>
        <Button variant="outline" onClick={apply} disabled={disabled || !yearId || !!busy} className="border-green-600 text-green-700" data-testid="cal-apply-btn">
          {busy === "apply" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CalendarCheck2 className="mr-1.5 h-4 w-4" />}Calcular aulas previstas
        </Button>
      </div>
      <div className="mb-3 flex flex-wrap items-end gap-2 rounded-lg bg-slate-50 p-3">
        <Input type="date" className="h-9 w-40" value={newDay.date} onChange={(e) => setNewDay((d) => ({ ...d, date: e.target.value }))} data-testid="cal-new-date" />
        <Select value={newDay.type} onValueChange={(v) => setNewDay((d) => ({ ...d, type: v }))}>
          <SelectTrigger className="h-9 w-44" data-testid="cal-new-type"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <Input className="h-9 w-56" placeholder="Descrição (ex.: Conselho de classe)" value={newDay.note} onChange={(e) => setNewDay((d) => ({ ...d, note: e.target.value }))} data-testid="cal-new-note" />
        <Button size="sm" variant="outline" onClick={add} disabled={!newDay.date} data-testid="cal-add-btn"><Plus className="mr-1 h-4 w-4" />Marcar dia</Button>
      </div>
      {days === null ? <Loader2 className="mx-auto h-5 w-5 animate-spin text-blue-700" /> : !days.length ? (
        <p className="py-6 text-center text-sm text-slate-400">Nenhum dia marcado. Envie o planejamento anual ou marque os dias à mão.</p>
      ) : (
        <div className="overflow-x-auto">
          <p className="mb-2 text-xs text-slate-500" data-testid="cal-count">{off} dia(s) sem aula · {days.length - off} dia(s) letivo(s) extra(s){meta.pending ? " · alterações não salvas" : ""}</p>
          <table className="w-full min-w-[640px] text-sm">
            <thead><tr className="border-b text-left text-xs font-semibold uppercase text-slate-500"><th className="py-1.5">Data</th><th>Tipo</th><th>Segue horário de</th><th>Descrição</th><th>Origem</th><th /></tr></thead>
            <tbody>
              {days.map((d) => (
                <DayRow key={d.date} d={d} onChange={(nd) => { setDays((x) => x.map((y) => (y.date === d.date ? nd : y))); setMeta((m) => ({ ...m, pending: true })); }}
                  onDelete={() => { setDays((x) => x.filter((y) => y.date !== d.date)); setMeta((m) => ({ ...m, pending: true })); }} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};
